import { Box, Container, Grid, Grow, Typography, useTheme } from '@mui/material';

import { ExperienceModel, useGetExperiences } from '@/logic/services/experiences';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';

import { ExperienceEmptyState } from '@/components/shared/experience-empty-state';
import React from 'react';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useGetSettings } from '@/logic/services';
import CardComponent from '@/components/shared/card-component';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const EmptyExperienceImgCard = '/icons/cinema/emptyExperienceImgCard.svg';

export const Experiences: NextPage<{
  serverCityId: number;
  path: string;
}> = ({ serverCityId, path }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { cityId: clientCityId } = useCity();

  const cityId = clientCityId || serverCityId;

  const { data: pageData } = useGetExperiences({
    params: { cityId },
  });

  const { data: seoDataList, isLoading } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.experiences);

  const { data: allCmsSetting } = useGetSettings();

  return (
    <>
      <Seo
        title={seoPage?.titleTag || intl.formatMessage({ id: 'experiences' })}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag || allCmsSetting?.experienceDescription}
        noFollow={seoPage?.hasFollow}
        noIndex={seoPage?.hasIndex}
      />
      <Container
        component='section'
        maxWidth='muviFixedContainer'
        sx={{
          paddingX: {
            mobile: '16px',
            tablet: '30px',
            mediumDesktop: '90px',
          },
          marginBottom: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='experiences' />
        </Typography>
        {!isLoading ? (
          <Box>
            {pageData?.experience?.length > 0 && allCmsSetting?.experienceDescription && (
              <Typography
                component='p'
                sx={{
                  fontSize: pxToRem(24),
                  color: theme.palette.grey[50],
                  textAlign: 'center',
                  mb: pxToRem(52),
                  maxWidth: '1094px',
                  mx: 'auto',
                  lineHeight: pxToRem(36),
                  [theme.breakpoints.down('tablet')]: {
                    mb: '24px',
                  },
                }}
              >
                {allCmsSetting.experienceDescription}
              </Typography>
            )}
            {pageData?.experience?.length > 0 ? (
              <Grid
                container
                rowSpacing={{
                  mobile: '16px',
                  tablet: pxToRem(48),
                }}
                columnSpacing={{
                  mobile: '0px',
                  tablet: pxToRem(46),
                }}
              >
                {pageData?.experience.map(
                  (experience: ExperienceModel, index: number) => (
                    <Grow
                      in
                      style={{
                        transformOrigin: '0 0 0',
                      }}
                      {...{ timeout: 1000 + index * 500 }}
                      key={experience.id}
                    >
                      <Grid item mobile={12} tablet={6} laptop={4}>
                        <CardComponent
                          alt={experience?.imageUrlAlt || experience?.name}
                          logoAlt={experience?.logoAlt || experience?.name}
                          image={experience.imageUrl || EmptyExperienceImgCard}
                          logo={experience.logo}
                          description={experience.description}
                          slug={experience.uniqueName}
                          linkName='experiences'
                        />
                      </Grid>
                    </Grow>
                  )
                )}
              </Grid>
            ) : (
              <ExperienceEmptyState />
            )}
          </Box>
        ) : (
          <MovieLoaderWrapper
            innerBox={{
              width: '160px',
              height: '160px',
              [theme.breakpoints.down('laptop')]: {
                width: '100px',
                height: '100px',
              },
            }}
            outerBox={{
              width: '100%',
              height: {
                mobile: 'calc(100vh - 56px)',
                tablet: 'calc(100vh - 100px)',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.req.cookies.city);

  const queryClient = new QueryClient();

  try {
    await useGetExperiences.fetchQuery(queryClient, {
      params: { cityId },
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      serverCityId: cityId,
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default Experiences;
